import { FC } from 'react'
import { cn } from '@wellesley/ui'
import { User } from '@wellesley/openapi'
import { AppsLink } from '@wellesley/router'
import { getUserNameWithDomain } from 'helpers/getUserNameWithDomain'

import { UserAvatar, UserAvatarProps } from '../UserAvatar'
import { selectUploadFile } from '@wellesley/uploads'

type UsersPreviewProps = {
  users: User[]
  className?: string
  avatarSize?: UserAvatarProps['size']
  borderWidth?: number
  isUserPreviewLink?: boolean
}

const DEFAULT_AVATAR_SIZE = 32

const UserAvatarItem: FC<{
  user: User
  avatarSize: UserAvatarProps['size']
}> = ({ user, avatarSize }) => {
  const avatarUrl =
    selectUploadFile({
      size: avatarSize || DEFAULT_AVATAR_SIZE,
      upload: user.avatar,
    })?.uri || ''

  return (
    <UserAvatar size={avatarSize} name={user.displayName} imgUrl={avatarUrl} />
  )
}

const UsersPreview: FC<UsersPreviewProps> = ({
  users,
  className,
  avatarSize = DEFAULT_AVATAR_SIZE,
  borderWidth = 4,
  isUserPreviewLink = true,
}) => {
  const avatarSizeWithBorder = avatarSize! + borderWidth * 2

  return (
    <div
      className={cn('relative flex items-center', className)}
      style={{
        left: -borderWidth,
        height: avatarSizeWithBorder,
        width: users.length * (avatarSize! - borderWidth) + borderWidth,
      }}
    >
      {users?.map((user, index) => (
        <div
          key={user.id}
          data-testid="user-avatar"
          className="absolute rounded-full border-white"
          style={{
            borderWidth: borderWidth,
            width: avatarSizeWithBorder,
            height: avatarSizeWithBorder,
            top: index * 0,
            left: index * (avatarSize! - borderWidth),
            zIndex: index + 1,
          }}
        >
          {isUserPreviewLink ? (
            <AppsLink
              to="/@/:username"
              props={{ username: getUserNameWithDomain(user) }}
            >
              <UserAvatarItem user={user} avatarSize={avatarSize} />
            </AppsLink>
          ) : (
            <UserAvatarItem user={user} avatarSize={avatarSize} />
          )}
        </div>
      ))}
    </div>
  )
}

export default UsersPreview
